import {
  createElement,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import type { ICreative } from '@services/types';

import { E_CREATIVE_TYPE, E_DESKTOP_FRAME_CONFIGS, E_DEVICE_TYPE, E_MOBILE_FRAME_CONFIGS } from '@common/configs';

import { GroupedSlide, SingleSlide } from './Slide';

export const useCreativesSlides = (creatives: ICreative[]): ReactNode[] => {
  return useMemo(() => {
    const slides: ReactNode[] = [];

    creatives.forEach(({ type, creative }, outerIdx) => {
      creative.forEach((item, innerIdx) => {
        const { campaign_name, start_date, end_date, images } = item;
        const keyPrefix = `${campaign_name ?? 'creative'}-${outerIdx}-${innerIdx}`;

        if (type === E_CREATIVE_TYPE.GROUPED && images.desktop?.length && images.mobile?.length) {
          slides.push(
            createElement(GroupedSlide, {
              key: `${keyPrefix}-grouped`,
              desktopImageSrc: images.desktop[0],
              mobileImageSrc: images.mobile[0],
              campaignName: campaign_name,
              startDate: start_date,
              endDate: end_date,
            })
          );
        } else {
          images.desktop?.forEach((url, idx) => {
            slides.push(
              createElement(SingleSlide, {
                key: `${keyPrefix}-desktop-${idx}`,
                deviceType: E_DEVICE_TYPE.DESKTOP,
                imageSrc: url,
                campaignName: campaign_name,
                startDate: start_date,
                endDate: end_date,
              })
            );
          });

          images.mobile?.forEach((url, idx) => {
            slides.push(
              createElement(SingleSlide, {
                key: `${keyPrefix}-mobile-${idx}`,
                deviceType: E_DEVICE_TYPE.MOBILE,
                imageSrc: url,
                campaignName: campaign_name,
                startDate: start_date,
                endDate: end_date,
              })
            );
          });
        }
      });
    });

    return slides;
  }, [creatives]);
};

export const useScaleChildToFitParent = (
  parentRef: RefObject<HTMLElement | null> | undefined,
  childDimensions: { clientWidth: number; clientHeight: number } | undefined,
  isGallery: boolean = false
) => {
  const [scale, setScale] = useState(0);

  const getSizes = useCallback(() => {
    const parentElement = parentRef?.current;
    if (!parentElement) {
      return;
    }

    const { clientHeight: parentH, clientWidth: parentW } = parentElement;

    if (parentH === 0 || parentW === 0) {
      return;
    }

    const { clientHeight: childH = 0, clientWidth: childW = 0 } = childDimensions ?? {};

    if (childH === 0 || childW === 0) {
      return;
    }

    const widthRatio = parentW / childW;
    const heightRatio = parentH / childH;
    const isPortrait = childH > childW;
    let reduction = 1;

    if (!isGallery) {
      reduction = isPortrait ? 0.9 : 0.75;
    }

    const calculatedScale = Math.min(widthRatio, heightRatio, 1) * reduction;
    setScale(calculatedScale);
  }, [childDimensions, parentRef, isGallery]);

  useLayoutEffect(() => {
    const parentElement = parentRef?.current;
    if (!parentElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(getSizes);
    resizeObserver.observe(parentElement);

    return () => {
      resizeObserver.unobserve(parentElement);
      resizeObserver.disconnect();
    };
  }, [getSizes, parentRef]);

  return { scale };
};

export const useGroupedFramesScale = (containerRef: RefObject<HTMLElement | null>, gapPx: number = 24) => {
  const [scale, setScale] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width <= 0 || height <= 0) {
        return;
      }

      const safeGap = Math.max(0, Math.min(gapPx, width * 0.12));
      const combinedWidth = E_DESKTOP_FRAME_CONFIGS.WIDTH + E_MOBILE_FRAME_CONFIGS.WIDTH;
      const maxHeight = Math.max(E_DESKTOP_FRAME_CONFIGS.HEIGHT, E_MOBILE_FRAME_CONFIGS.HEIGHT);

      const widthScale = (width - safeGap) / combinedWidth;
      const heightScale = height / maxHeight;

      const nextScale = Math.max(
        0,
        Math.min(widthScale, heightScale, E_DESKTOP_FRAME_CONFIGS.MAX_SCALE, E_MOBILE_FRAME_CONFIGS.MAX_SCALE)
      );

      setScale(nextScale);
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [containerRef, gapPx]);

  return scale;
};

export default useCreativesSlides;
